import { PROVIDER_API, USE_MOCK_AUTH } from "../config/api";
import { cleanGhanaPhone } from "../utils/ghanaPhone";
import { normalizeProviderUser } from "../utils/normalizeProviderUser";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";

/** Institution account may update these columns on `providers` (not SLA fields). */
export const INSTITUTION_PROFILE_KEYS = [
  "legal_name",
  "business_type",
  "region",
  "city",
  "gps_address",
  "primary_email",
  "primary_phone_number",
  "secondary_email",
  "secondary_phone_number",
  "website",
  "logo",
  "socials",
  "channels",
];

function normalizeScalar(key, value) {
  if (value == null) return value;
  if (typeof value === "number" || typeof value === "boolean") return value;
  const trimmed = String(value).trim();
  if (key === "primary_email" || key === "secondary_email" || key === "email") {
    return trimmed.toLowerCase();
  }
  if (
    key === "primary_phone_number" ||
    key === "secondary_phone_number" ||
    key === "phone" ||
    key === "phone_number"
  ) {
    return cleanGhanaPhone(trimmed) || trimmed.replace(/^\+/, "").replace(/\s+/g, "");
  }
  return trimmed;
}

function valuesEqual(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (typeof a === "object" || typeof b === "object") {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  }
  return String(a) === String(b);
}

export function buildProfilePatch(next, baseline = {}, allowedKeys = INSTITUTION_PROFILE_KEYS) {
  const patch = {};
  for (const key of allowedKeys) {
    if (!(key in next) && next[key] === undefined) continue;
    const nextValue =
      key === "socials" || key === "channels" || key === "logo"
        ? next[key]
        : normalizeScalar(key, next[key]);
    const baseValue =
      key === "socials" || key === "channels" || key === "logo"
        ? baseline[key]
        : normalizeScalar(key, baseline[key]);
    if (!valuesEqual(nextValue, baseValue)) {
      patch[key] = nextValue;
    }
  }
  return patch;
}

function localUserPatch(patch, baseline = {}) {
  const result = { ...patch };
  if (patch.legal_name != null) {
    result.name = patch.legal_name;
    result.institution = patch.legal_name;
  }
  if (patch.primary_email != null) {
    result.email = patch.primary_email;
    result.primary_email = patch.primary_email;
  }
  if (patch.primary_phone_number != null) {
    const phone = patch.primary_phone_number;
    result.primary_phone_number = phone;
    result.phone_number = phone;
    result.phone = phone?.startsWith?.("+") ? phone : phone ? `+${phone}` : phone;
  }
  if (patch.business_type != null) {
    result.business_type = patch.business_type;
    result.type = patch.business_type;
  }
  if (patch.gps_address != null) result.gps_address = patch.gps_address;
  if (patch.logo !== undefined) result.logo = patch.logo;
  if (patch.channels) result.channels = patch.channels;
  if (patch.socials) result.socials = patch.socials;
  return { ...baseline, ...result };
}

/** GET /api/provs/profile — full institution row for settings. */
export async function fetchProviderProfile(token) {
  if (USE_MOCK_AUTH || !token) {
    await new Promise((r) => setTimeout(r, 200));
    if (USE_MOCK_AUTH) {
      const { MOCK_PROVIDER_PROFILE } = await import("./mockAuth");
      return MOCK_PROVIDER_PROFILE ? normalizeProviderUser(MOCK_PROVIDER_PROFILE) : null;
    }
    return null;
  }

  const json = await apiRequest(PROVIDER_API.profile, { method: "GET", token });
  const data = unwrapApiEnvelope(json);
  const institution =
    data?.institution && typeof data.institution === "object"
      ? data.institution
      : data && typeof data === "object"
        ? data
        : null;

  return institution ? normalizeProviderUser(institution) : null;
}

/** PUT /api/provs/profile — only changed fields. */
export async function updateProviderProfile(token, nextForm, baselineForm = {}) {
  const body = buildProfilePatch(nextForm, baselineForm, INSTITUTION_PROFILE_KEYS);

  if (!Object.keys(body).length) {
    throw new Error("No profile changes to save.");
  }

  if (USE_MOCK_AUTH || !token) {
    await new Promise((r) => setTimeout(r, 450));
    return localUserPatch(body, baselineForm);
  }

  const json = await apiRequest(PROVIDER_API.profile, {
    method: "PUT",
    token,
    body,
  });

  const data = unwrapApiEnvelope(json);
  const institution =
    data?.institution && typeof data.institution === "object"
      ? data.institution
      : data && typeof data === "object"
        ? data
        : null;

  if (institution) {
    const normalized = normalizeProviderUser(institution);
    return {
      ...localUserPatch(body, baselineForm),
      ...(normalized || {}),
    };
  }

  return localUserPatch(body, baselineForm);
}
